// Number, money and date display (ADR-082, ADR-083).
// Digits are always 0-9. Separators follow the language: Arabic and English 1,320.50 — French 1 320,50.
// Money is the Algerian dinar with 2 decimals and its symbol after the amount: د.ج (Arabic), DA (French, English).
// Business dates and "today" use the Africa/Algiers time zone. Amounts arrive from the database as strings
// (numeric) and are only displayed here: nothing computed on this side is ever posted.
import type { Language } from '../i18n';

export const BUSINESS_TIME_ZONE = 'Africa/Algiers';
const CURRENCY_SYMBOL: Record<Language, string> = { ar: 'د.ج', fr: 'DA', en: 'DA' };
/** Keeps the amount and its symbol on one line. */
const NO_BREAK_SPACE = String.fromCharCode(0xa0);
const NUMBER_LOCALE: Record<Language, string> = { ar: 'en-US', fr: 'fr-FR', en: 'en-US' };

function toNumber(value: number | string): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) throw new Error(`Not a number: ${String(value)}`);
  return n;
}

/** 1320.5 → "1,320.50" (ar, en) or "1 320,50" (fr). */
export function formatAmount(value: number | string, lang: Language): string {
  return new Intl.NumberFormat(NUMBER_LOCALE[lang], { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    toNumber(value),
  );
}

/** Minus sign (U+2212) behind a left-to-right mark, so it stays before the digits inside Arabic text. */
const MINUS = String.fromCharCode(0x200e, 0x2212);

/** 1320.5 → "1,320.50 د.ج" / "1 320,50 DA" / "1,320.50 DA". The symbol always follows the number; a negative
 * amount reads "−1,000.00 د.ج" in every language. */
export function formatMoney(value: number | string, lang: Language): string {
  const n = toNumber(value);
  const sign = n < 0 ? MINUS : '';
  return `${sign}${formatAmount(Math.abs(n), lang)}${NO_BREAK_SPACE}${CURRENCY_SYMBOL[lang]}`;
}

/** Whole quantities and counts: 1240 → "1,240" / "1 240". */
export function formatCount(value: number, lang: Language): string {
  if (!Number.isInteger(value)) throw new Error(`Quantities are whole numbers: ${value}`);
  return new Intl.NumberFormat(NUMBER_LOCALE[lang], { maximumFractionDigits: 0 }).format(value);
}

/** A business date "YYYY-MM-DD" → "2026-10-11" (ar), "11/10/2026" (fr), "11 Oct 2026" (en). No time-zone shift: it is already a business date. */
export function formatDate(isoDate: string, lang: Language): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) throw new Error(`Not a business date: ${isoDate}`);
  const [, y, mo, d] = m;
  if (lang === 'ar') return isoDate;
  if (lang === 'fr') return `${d}/${mo}/${y}`;
  const month = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(Number(y), Number(mo) - 1, 1)),
  );
  return `${Number(d)} ${month} ${y}`;
}

/** A moment (timestamp) shown in business time: date as formatDate plus "HH:mm". */
export function formatDateTime(moment: Date | string, lang: Language): string {
  const date = typeof moment === 'string' ? new Date(moment) : moment;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const sep = lang === 'en' ? ', ' : ' ';
  return `${formatDate(`${get('year')}-${get('month')}-${get('day')}`, lang)}${sep}${get('hour')}:${get('minute')}`;
}

/** Today's business date in Africa/Algiers, as "YYYY-MM-DD". */
export function businessToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}
